import { getAuth, signOut } from "firebase/auth";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getChallengesLink,
  getCreateChallengeLink,
  getCreateProjectLink,
  getEditProjectPage,
  getEditTeamProjectUrl,
  getFeedPage,
  getProjectDetailsPageUrl,
  getTeamProjectDetailsPageUrl,
  getTeamProfileLink,
  getProfileSearchURL,
  getChallengeDetailsLink,
  getProjectFullPageUrl,
  getCreateTeamURL,
  getEditProfileBaseURL,
  getEditProfileAboutURL,
  getProfileURL,
  getProfileLikedProjectsURL,
  getProfileProjectsURL,
} from "../util/navigation.util";
import { dispatchLogout } from "../store/dispatcher";
import { getHistory, getPreviousPath } from "../store/slices/userSessionSlice";
import useAnalytics from "./useAnalytics";
import { useAppSelector } from "../store/hooks";

/**
 * Navigation links
 * @param user
 * @returns
 */
export default function useAppNavigation() {
  const prevPath = useAppSelector(getPreviousPath);
  const history = useAppSelector(getHistory);

  // use app navigation
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // use analytics
  const { trackEditProject, trackProjectFullPage } = useAnalytics();

  // helper function for navigation
  const navTo = (url: string) => navigate(url);

  /**
   * Signs user out and removes user
   * session data
   */
  const handleSignOut = async () => {
    // get user session
    const auth = getAuth();
    // sign out user
    await signOut(auth);
    dispatchLogout();
  };

  // go to page for updating user settings
  const navToProfileSettings = () => {
    navTo("/account/edit/profile");
  };
  // go to user profile page
  const navToProfile = (id: string) => navTo(getProfileURL(id));
  // go to profile projects
  const navToProfileProjects = (id: string) => navTo(getProfileProjectsURL(id));
  // go to profile liked projects
  const navToProfileLikedProjects = (id: string) =>
    navTo(getProfileLikedProjectsURL(id));
  // go to search results page
  const navToSearchResults = (searchValue: string) => {
    navTo(getProfileSearchURL(searchValue));
  };

  // go to project page
  const navigateToPost = (id: string) => navTo(`/projects/post/${id}`);

  /**
   * Takes user to code editor page to edit project
   */
  const navigateToEditPost = (id: string) => {
    trackEditProject();
    navTo(getEditProjectPage(id));
  };

  /**
   * Redirects user to the page with the project
   * full view
   * @param id
   */
  const navigateToProjectFullPage = (id: string) => {
    trackProjectFullPage();
    navTo(getProjectFullPageUrl(id));
  };

  /**
   * Redirects user to the page with the project
   * details. This includes the project comment section. If comment id is provided, it will scroll to the comment
   * @param id
   * @param comment optional comment id
   * @returns
   */
  const navigateToProjectDetailsPage = (id: string, comment?: string) => {
    navTo(getProjectDetailsPageUrl(id, comment));
  };

  /**
   * Redirects user to the page with the team project
   * details. This includes the project comment section.
   * @param id
   */
  const navigateToTeamProjectDetailsPage = (id: string, comment?: string) => {
    navTo(getTeamProjectDetailsPageUrl(id, comment));
  };

  /**
   * Redirects user to the page with the team project
   * full view
   * @param id
   */
  const navigateToTeamProjectFullPage = (id: string) => {
    navTo(`/team-project/full-page/${id}`);
  };

  /**
   * Redirect user to edit team project page
   */
  const navigateToEditTeamProject = (teamId: string, projectId: string) => {
    navTo(getEditTeamProjectUrl(teamId, projectId));
  };

  /**
   *
   */
  const navigateToTeamPage = (teamId: string) =>
    navTo(getTeamProfileLink(teamId));

  /**
   *
   */
  const navigateToFeed = (type: string) => {
    navTo(getFeedPage(type));
  };

  /**
   * Takes user to their profile page.
   * It contains the about and interests section
   */

  /**
   * Takes user to their profile page with their teams
   * @param id
   */
  // const navigateToProfileTeams = (filter = "your-teams") => {
  //   navTo(getUserProfileTeamsLink(user?._id || "", filter));
  // };

  /**
   * Takes user to challenges page
   * @param id
   */
  const navigateToChallenges = () => {
    navTo(getChallengesLink());
  };

  /**
   * Takes user to editor page where they can edit the code for
   * their profile page layout.
   */
  const navigateToEditProfileLayout = () => {
    navTo(getEditProfileBaseURL());
  };

  /**
   * Takes user to editor page where they can edit the code for the
   * about section in their profile page
   * @returns
   */
  const navigateToEditProfileAbout = () => navTo(getEditProfileAboutURL());

  /**
   * Takes user to profile page with the profile posts feed
   */
  const navigateToProfilePosts = (id: string) =>
    navTo(getProfileProjectsURL(id));

  /**
   * Takes user to editor page to create new
   * project
   */
  const navigateToCreateProject = () => {
    navTo(getCreateProjectLink());
  };

  /**
   * Takes user to page to create new challenge
   */
  const navigateToCreateChallenge = () => navTo(getCreateChallengeLink());

  // nav to page for creating new teams
  const navToCreateTeam = () => navTo(getCreateTeamURL());

  /**
   * Takes user to page to view challenge details
   */
  const navigateToViewChallengeDetails = (id: string) =>
    navTo(getChallengeDetailsLink(id));

  /**
   * Returns user to previous screen in navigation stack if
   * more than 1 route exists in navigation history. If only
   * one route exists, user is sent to home page
   * If default url is provided, user is sent to that page instead
   * @param defaultUrl page to send user instead of home
   */
  const goBack = (defaultUrl?: string) => {
    const { length } = history;

    // if we have more than one path in history
    if (length > 1) {
      // go to home since previous path is the same as current page
      if (history[history.length - 2] === pathname) {
        navigate(defaultUrl || "/");
      } else {
        // go the previous page
        navigate(history[history.length - 2]);
      }
    } else if (history[0] !== pathname && history[0] !== `${pathname}?`) {
      // go to stored previous path since its different
      navigate(prevPath);
    } else {
      navigate(defaultUrl || "/");
    }
  };

  return {
    goBack,
    navTo,
    navToProfileProjects,
    navToProfileLikedProjects,
    navigateToFeed,
    navigateToPost,
    navigateToEditPost,
    navigateToProjectFullPage,
    navigateToEditTeamProject,
    navigateToTeamPage,
    navigateToTeamProjectFullPage,
    navToProfile,
    navToProfileSettings,
    navigateToEditProfileLayout,
    navigateToProfilePosts,
    navigateToEditProfileAbout,
    navigateToCreateProject,
    navigateToCreateChallenge,
    navigateToViewChallengeDetails,
    navigateToChallenges,
    navToSearchResults,
    navigateToProjectDetailsPage,
    navigateToTeamProjectDetailsPage,
    navToCreateTeam,
    handleSignOut,
    links: {
      teamPage: (id: string) => `/teams/${id}`,
    },
  };
}
